import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertTriangle, Loader2 } from "lucide-react";
import Modal from "../components/modal";

const InterviewNavigationGuardContext = createContext({
  registerInterviewDraft: () => {},
  requestInterviewNavigation: (callback) => callback?.(),
});

export const InterviewNavigationGuardProvider = ({ children }) => {
  const intl = useIntl();
  const [draft, setDraft] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const close = useCallback(() => {
    if (busy) return;
    setIsOpen(false);
    setPendingAction(null);
    setError("");
  }, [busy]);

  const finishNavigation = useCallback(() => {
    const action = pendingAction;
    setIsOpen(false);
    setPendingAction(null);
    setError("");
    action?.();
  }, [pendingAction]);

  const requestInterviewNavigation = useCallback((callback) => {
    if (!draft?.hasQueuedChanges) {
      callback?.();
      return;
    }
    setPendingAction(() => callback);
    setError("");
    setIsOpen(true);
  }, [draft]);

  const saveAndContinue = async () => {
    if (!draft) return;
    setBusy(true);
    setError("");
    const saved = await draft.saveQueuedChanges();
    setBusy(false);
    if (!saved) {
      setError(draft.validationError || intl.formatMessage({ id: "teamroom.builder.saveFailed" }));
      return;
    }
    finishNavigation();
  };

  const discardAndContinue = () => {
    draft?.discardQueuedChanges();
    finishNavigation();
  };

  const value = useMemo(() => ({
    registerInterviewDraft: setDraft,
    requestInterviewNavigation,
  }), [requestInterviewNavigation]);

  return (
    <InterviewNavigationGuardContext.Provider value={value}>
      {children}
      <Modal open={isOpen} onClose={close} maxWidth="max-w-[500px]" dismissable={!busy}>
        <div className="p-8 text-center">
          <span className="w-14 h-14 rounded-2xl bg-standart-ocean/10 text-standart-ocean inline-flex items-center justify-center mx-auto">
            <AlertTriangle size={26} strokeWidth={2.2} />
          </span>
          <h3 className="text-xl font-bold text-navy-blue mt-4"><FormattedMessage id="teamroom.builder.guard.title" /></h3>
          <p className="text-gray-500 text-[14px] mt-2 leading-relaxed"><FormattedMessage id="teamroom.builder.guard.body" /></p>
          {error && <p className="mt-4 text-[13.5px] font-bold text-[#ef6461]">{error}</p>}
          <div className="flex items-center justify-center gap-3 mt-6">
            <button disabled={busy} onClick={close} className="h-11 px-5 rounded-full bg-gray-100 text-navy-blue font-bold text-[13px] hover:bg-gray-200 transition disabled:opacity-50"><FormattedMessage id="teamroom.builder.guard.keepEditing" /></button>
            <button disabled={busy} onClick={discardAndContinue} className="h-11 px-5 rounded-full bg-[#ef6461]/10 text-[#ef6461] font-bold text-[13px] hover:bg-[#ef6461]/15 transition disabled:opacity-50"><FormattedMessage id="teamroom.builder.guard.discard" /></button>
            <button disabled={busy} onClick={saveAndContinue} className="h-11 px-5 rounded-full bg-standart-ocean text-white font-bold text-[13px] hover:brightness-95 transition disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 size={15} className="animate-spin" />} <FormattedMessage id="teamroom.builder.guard.save" />
            </button>
          </div>
        </div>
      </Modal>
    </InterviewNavigationGuardContext.Provider>
  );
};

export const useInterviewNavigationGuard = () => useContext(InterviewNavigationGuardContext);
